<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatAmount, toIsoDate } from '../utils/format'
import type { CurrencyExposureRow, CurrencyExposureTotals } from '../types/finance'

const { t } = useI18n()
const { currencyExposure } = useFinanceApi()

const rows = ref<CurrencyExposureRow[]>([])
const totals = ref<CurrencyExposureTotals | null>(null)
const loading = ref(true)
const error = ref('')
const asOfDate = ref(toIsoDate())

const baseCurrencyCode = computed(
  () => totals.value?.base_currency_code || totals.value?.base_currency || '',
)

const maxAbsExposure = computed(() => {
  const values = rows.value.map((row) => Math.abs(Number(row.net_exposure ?? 0)))
  return values.length ? Math.max(...values) : 0
})

function barWidth(value: number): string {
  if (!maxAbsExposure.value) return '0%'
  return `${Math.min(100, (Math.abs(value) / maxAbsExposure.value) * 100)}%`
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await currencyExposure(asOfDate.value)
    rows.value = result.rows
    totals.value = result.summary
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.nav.currencyExposure') }}</h2>
  </div>

  <div class="toolbar">
    <input v-model="asOfDate" class="date-input" type="date">
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.apply') }}
    </button>
    <span class="spacer" />
    <div class="summary">
      <span v-if="baseCurrencyCode">
        {{ t('finance.reports.baseCurrency') }}: <strong>{{ baseCurrencyCode }}</strong>
      </span>
      <span v-if="totals">
        {{ t('finance.reports.currenciesCount') }}: <strong>{{ totals.currencies_count }}</strong>
      </span>
    </div>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else>
    <section v-if="totals?.warnings?.length" class="notice">
      <strong>{{ t('finance.reports.warnings') }}</strong>
      <p v-for="warning in totals.warnings" :key="warning">{{ warning }}</p>
    </section>

    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.reports.netExposure') }}</span>
        <span class="summary-value">
          {{ formatAmount(totals?.net_exposure_total ?? 0) }}
          <small v-if="baseCurrencyCode">{{ baseCurrencyCode }}</small>
        </span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.reports.customerAdvances') }}</span>
        <span class="summary-value">
          {{ formatAmount(totals?.customer_advances_total_in_base_currency ?? 0) }}
          <small v-if="baseCurrencyCode">{{ baseCurrencyCode }}</small>
        </span>
      </div>
    </div>

    <div v-if="rows.length === 0" class="state-message"><p>{{ t('finance.reports.empty') }}</p></div>

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.common.currency') }}</th>
            <th class="cell-num">{{ t('finance.reports.receivablesAmount') }}</th>
            <th class="cell-num">{{ t('finance.reports.payablesAmount') }}</th>
            <th class="cell-num">{{ t('finance.reports.cashAmount') }}</th>
            <th class="cell-num">{{ t('finance.reports.customerAdvances') }}</th>
            <th class="cell-num">{{ t('finance.reports.netExposure') }}</th>
            <th>{{ t('finance.common.detail') }}</th>
            <th class="cell-num">{{ t('finance.reports.fxRate') }}</th>
            <th class="cell-num">{{ t('finance.reports.netExposure') }} ({{ baseCurrencyCode }})</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.currency_code">
            <td class="cell-name cell-code">{{ row.currency_code }}</td>
            <td class="cell-num">{{ formatAmount(row.receivables_amount) }}</td>
            <td class="cell-num">{{ formatAmount(row.payables_amount) }}</td>
            <td class="cell-num">{{ formatAmount(row.cash_amount) }}</td>
            <td class="cell-num">{{ formatAmount(row.customer_advances_amount) }}</td>
            <td class="cell-num" :class="Number(row.net_exposure) < 0 ? 'is-negative' : 'is-positive'">
              {{ formatAmount(row.net_exposure) }}
            </td>
            <td>
              <div class="bar-track">
                <div
                  class="bar-fill"
                  :class="{ 'is-negative': Number(row.net_exposure) < 0 }"
                  :style="{ width: barWidth(row.net_exposure) }"
                />
              </div>
            </td>
            <td class="cell-num">
              <template v-if="row.fx_rate_to_base !== null">
                {{ formatAmount(row.fx_rate_to_base, 4) }}
                <span class="cell-sub">{{ formatDisplayDate(row.fx_rate_date) }}</span>
              </template>
              <span v-else class="empty-inline">—</span>
            </td>
            <td class="cell-num">
              {{ row.net_exposure_in_base_currency === null
                ? '—'
                : formatAmount(row.net_exposure_in_base_currency) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>
</template>
